import React from 'react';
import { Loader2 } from 'lucide-react';
import type { ContactStatePill, StateDimension, StateFact } from '@shared/schemas/flow-contact-state';
import {
  GONG_FLOW_LABEL,
  QUEUED_LABEL,
  GIFT_LABEL,
  RECENT_CAMPAIGN_LABEL,
  UPCOMING_CAMPAIGN_LABEL,
  SUGGESTED_INVITE_LABEL,
  RECENT_REQUEST_LABEL,
  WARM_INTRO_LABEL,
  RECENT_QUEUE_SEND_LABEL,
} from '@shared/flow/pill-variants';
import {
  factCardStyle as sharedFactCardStyle,
  queuedCardOwnerName,
  OPEN_TASK_LABEL,
  RECENT_TASK_LABEL,
  pillLabelForFact,
  isGongLinkedOpenTaskFact,
} from './stateFactPresentation';
import { useAddToQueueFromSuggestion, useWarmIntroAdd, useCampaignMemberOpen } from './useStateFactActions';
import { resolveEvidenceLink } from '../state-links';
import { useEnrollQueue } from '../EnrollQueue/EnrollQueueContext';
import { formatRelativeTime } from '../EnrollQueue/request-format';
import { useSegmentTrack } from '../../../hooks/useSegmentTrack';
import { ANALYTICS_EVENTS, type Surface } from '@shared/analytics/events';

/**
 * Pill strip (ContactFlowTable's "Terra Actions" column) + fact cards (the
 * ContactDetail "Terra Actions" strip, ContactDetailPanels.tsx) for the
 * contact-state read-model (PR 2, 2026-07-23 decisions + revision 3, FINAL).
 *
 * Dimension → icon + Clay semantic tone is the one fixed UI decision here;
 * pill TEXT comes from the wire's `pill.label` (adapter-declared, e.g.
 * "Gong Flow" / "Recent Campaign" / "Live Task" — see
 * src/services/flow/contact-state.ts aggregatePills) so backend copy stays
 * the single source of truth. `label` below is a defensive fallback only
 * (ContactStatePillSchema.label is non-optional; a StateFact has no `label`
 * field at all, so StateFactCard always uses it).
 *
 * Only 'gifted' has no registered adapter yet
 * (src/shared/schemas/flow-contact-state.ts StateDimensionSchema comment) —
 * it cannot appear on a real pill/fact today, but this map is total over
 * the wire enum so the file compiles against future adapters without an
 * update here. Icon/tone choices for those two are this component's own
 * call (no spec'd copy) — see the implementation report's Design decisions.
 */

interface DimensionStyle {
  icon: string;
  bg: string;
  tx: string;
  label: string;
}

/**
 * `OPEN_TASK_LABEL` (unified display label for BOTH open-task pill variants)
 * now lives in `stateFactPresentation.ts` (plan 005 U1c lift) — imported
 * back above. See that module's doc comment for the full 2026-07-30
 * display-unification rationale.
 */

const DIMENSION_STYLES: Record<StateDimension, DimensionStyle> = {
  // Same blue tone the old inline "⚡ In flow" name-chip used before this
  // pill superseded and REMOVED it (ContactFlowTable.tsx RENDERERS.name,
  // 2026-07-23 revision 1) — same signal, same color family.
  in_flow: {
    icon: '⚡',
    bg: 'bg-bg-blue-light',
    tx: 'text-content-action dark:text-blueberry-400',
    label: GONG_FLOW_LABEL,
  },
  invited: {
    icon: '✉',
    bg: 'bg-matcha-50 dark:bg-matcha-900/30',
    tx: 'text-matcha-700 dark:text-matcha-400',
    label: RECENT_CAMPAIGN_LABEL,
  },
  open_task: {
    icon: '☑',
    bg: 'bg-tangerine-50 dark:bg-tangerine-900/30',
    tx: 'text-tangerine-700 dark:text-tangerine-400',
    label: OPEN_TASK_LABEL,
  },
  queued: {
    icon: '●',
    bg: 'bg-nightshade-100 dark:bg-nightshade-800',
    tx: 'text-nightshade-600 dark:text-nightshade-400',
    label: QUEUED_LABEL,
  },
  gifted: {
    icon: '🎁',
    bg: 'bg-ube-50 dark:bg-ube-900/30',
    tx: 'text-ube-700 dark:text-ube-400',
    label: GIFT_LABEL,
  },
  // Neutral default for the `recent_request` dimension (flow-workflow-
  // requests U8) — used for BOTH a plain action with no staged output-
  // binding value AND a completed run that staged one. There used to be a
  // second amber "awaiting sync" variant here; it was removed as a product
  // decision (flow-workflow-requests, 2026-07-27) because nothing checks the
  // real write-back to the system of record, so the tag never actually
  // resolved and was misleading. The staged value itself still shows (via
  // the wire `pill.label`/fact `status` text) — only the special styling and
  // "Awaiting sync" tag are gone.
  recent_request: {
    icon: '●',
    bg: 'bg-neutral-100 dark:bg-nightshade-800/60',
    tx: 'text-content-secondary dark:text-nightshade-400',
    label: RECENT_REQUEST_LABEL,
  },
  // `warm_intro` (warm-intro plan U6, R3) — a neutral pill container (same
  // family as SUGGESTED_INVITE_STYLE/RECENT_TASK_STYLE below) with a
  // blueberry accent icon/text: distinct from `in_flow`'s saturated blue
  // background so the two dimensions never read as the same signal. `label`
  // here is a defensive fallback only (see this map's file-header comment) —
  // the real pill text always comes from the wire `pill.label`
  // ("Warm Intro: {Connector Name}"), which is never empty for this
  // dimension (warmIntroSource drops a nameless row before it can produce a
  // fact). REVIEW-THESE default: icon/tone are this component's own call,
  // per the /create-play-type-adjacent unit's approach note (never the
  // prototype's portability hex — Clay semantic tokens only, index.css).
  warm_intro: {
    icon: '🤝',
    bg: 'bg-neutral-100 dark:bg-nightshade-800/60',
    tx: 'text-content-action dark:text-blueberry-400',
    label: WARM_INTRO_LABEL,
  },
  // `recent_queue_send` (2026-09-01 flow-contacts-bulk-send plan U4, Decision
  // 11) — pomegranate red (the danger family, index.css `--color-*-danger`
  // tokens), deliberately LOUDER than `invited`'s quiet matcha and than the
  // flow-glaze prototype's teal `recent_invite` treatment (see that plan's
  // Scope Boundaries deviation callout) — this pill flags "we already
  // touched this contact recently", which should read as a caution, not an
  // ambient info chip. 🚩 avoids duplicating `invited`'s ✉ icon.
  // REVIEW-THESE default (icon/tone are this component's own call, per the
  // skill's step 2 sign-off note) — flag for Spencer QA.
  recent_queue_send: {
    icon: '🚩',
    bg: 'bg-pomegranate-50 dark:bg-pomegranate-900/30',
    tx: 'text-pomegranate-700 dark:text-pomegranate-400',
    label: RECENT_QUEUE_SEND_LABEL,
  },
};

/**
 * Fix A (Spencer QA — "suggested and recent invite are two different types.
 * It should just say suggested invite instead of recent."). `SUGGESTED_INVITE_LABEL`
 * / `UPCOMING_CAMPAIGN_LABEL` / `RECENT_TASK_LABEL` are imported above from
 * src/shared/flow/pill-variants.ts (bd-ozirv.7) — this module used to carry
 * its own hand-synced copies of these constants plus four predicates
 * (`isSuggestedInviteTier` / `isUpcomingCampaignTier` / `isClosedTaskDetail` /
 * `isGongLinkedTaskDetail`) and their narrow Zod schemas that re-derived the
 * SAME fact→label branches the server's `pillLabelForFact` already computes.
 * `pillStyle`/`factCardStyle` below now call the shared `pillLabelForFact` /
 * `isGongLinkedOpenTaskFact` directly instead.
 */
const SUGGESTED_INVITE_STYLE: DimensionStyle = {
  icon: '○',
  bg: 'bg-neutral-100 dark:bg-nightshade-800/60',
  tx: 'text-content-secondary dark:text-nightshade-400',
  label: SUGGESTED_INVITE_LABEL,
};

/**
 * The "Upcoming Campaign" fact-card variant — same matcha-green tone as the
 * default `invited` ("Recent Campaign") style, only the label differs. Reusing
 * the base style keeps a single source of truth for the invited look; a future
 * distinct treatment for upcoming events would change only this object.
 */
const UPCOMING_CAMPAIGN_STYLE: DimensionStyle = {
  ...DIMENSION_STYLES.invited,
  label: UPCOMING_CAMPAIGN_LABEL,
};

/**
 * Distinct icon/tone for the closed-task ("Recent Task") variant of the
 * `open_task` dimension — deliberately QUIETER than Live Task's tangerine
 * (a closed task is resolved, lower-urgency than an open one): a neutral
 * family, same tone level as `SUGGESTED_INVITE_STYLE` above.
 */
const RECENT_TASK_STYLE: DimensionStyle = {
  icon: '✓',
  bg: 'bg-neutral-100 dark:bg-nightshade-800/60',
  tx: 'text-content-secondary dark:text-nightshade-400',
  label: RECENT_TASK_LABEL,
};

/**
 * The style for one ContactStatePill — keyed on the wire `label`, NOT a new
 * dimension value (Fix A's split keeps ONE dimension, 'invited', with TWO
 * possible pill labels — see contact-state.ts's `pillLabel`/aggregatePills;
 * the Recent Task variant mirrors this for 'open_task'). Narrowly matched
 * (dimension + label together) so this can never misfire for an unrelated
 * dimension whose pill label happens to collide with one of these strings.
 *
 * The Gong Task variant (wire `pill.label === 'Gong Task'`) is DELIBERATELY
 * NOT branched here anymore (Spencer, 2026-07-30 display-unification
 * decision) — it now falls through to the default `DIMENSION_STYLES.open_task`
 * (uniform ☑ tangerine, no ⚡), same as a direct "Live Task" pill. The wire
 * label itself is unchanged (still drives `PILL_DIMENSION_PRIORITY` ranking
 * server-side) — only the STYLE is unified. See `OPEN_TASK_LABEL`'s comment
 * above and the `label` override in `StatePill` below for the matching
 * display-text unification.
 */
function pillStyle(pill: ContactStatePill): DimensionStyle {
  if (pill.dimension === 'invited' && pill.label === SUGGESTED_INVITE_LABEL) {
    return SUGGESTED_INVITE_STYLE;
  }
  if (pill.dimension === 'open_task' && pill.label === RECENT_TASK_LABEL) {
    return RECENT_TASK_STYLE;
  }
  return DIMENSION_STYLES[pill.dimension];
}

/**
 * V1's local `StateFactCard` style — pairs the shared label/icon derivation
 * (`stateFactPresentation.ts`'s `factCardStyle`, plan 005 U1c lift) with
 * V1's own raw-palette bg/tx classes (deliberately V1-only — a Glaze surface
 * maps dimension → semantic token itself, never these Tailwind literals).
 * Dispatches on the SHARED label so this can never drift from the shared
 * derivation's variant boundaries; see that module's `factCardStyle` doc
 * comment for the full branch-by-branch rationale.
 *
 * `queuedCardOwnerName` also now lives in `stateFactPresentation.ts` —
 * imported back above.
 */
function factCardStyle(fact: StateFact): DimensionStyle {
  const shared = sharedFactCardStyle(fact);
  if (shared.label === SUGGESTED_INVITE_LABEL) return SUGGESTED_INVITE_STYLE;
  if (shared.label === UPCOMING_CAMPAIGN_LABEL) return UPCOMING_CAMPAIGN_STYLE;
  if (shared.label === RECENT_TASK_LABEL) return RECENT_TASK_STYLE;
  return DIMENSION_STYLES[fact.dimension];
}

function fmtDateTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function pillTooltip(pill: ContactStatePill): string {
  const parts = [`freshness: ${pill.freshness}`];
  const latest = fmtDateTime(pill.latest_at);
  if (latest) parts.push(`latest: ${latest}`);
  const asOf = fmtDateTime(pill.as_of);
  if (asOf) parts.push(`as of: ${asOf}`);
  return parts.join(' · ');
}

/**
 * Display-only override (Spencer, 2026-07-30): an open (non-Recent-Task)
 * `open_task` pill always displays OPEN_TASK_LABEL regardless of which wire
 * label it carries ('Live Task' direct vs 'Gong Task' gong-linked) — the
 * wire `pill.label` itself is UNCHANGED and still drives
 * `PILL_DIMENSION_PRIORITY` ranking server-side; only what's RENDERED is
 * unified. Recent Task (closed) and every other dimension keep the existing
 * wire-label-first fallback. Shared by `StatePill` and the D15 overflow
 * chip's tooltip so a hidden pill's name matches what it would have shown
 * had it been visible.
 */
function pillDisplayLabel(pill: ContactStatePill): string {
  if (pill.dimension === 'open_task' && pill.label !== RECENT_TASK_LABEL) {
    return OPEN_TASK_LABEL;
  }
  return pill.label || pillStyle(pill).label;
}

export function StatePill({ pill }: { pill: ContactStatePill }) {
  const style = pillStyle(pill);
  const label = pillDisplayLabel(pill);
  return (
    <span
      title={pillTooltip(pill)}
      data-testid="state-pill"
      data-dimension={pill.dimension}
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10.5px] font-semibold whitespace-nowrap ${style.bg} ${style.tx}`}
    >
      <span aria-hidden="true">{style.icon}</span>
      {label}
      {pill.count > 1 && <span className="ml-0.5 opacity-70 tabular-nums">×{pill.count}</span>}
    </span>
  );
}

/**
 * D15 (flow-contacts-bulk-send QA round 3, item 1) — caps the visible pill
 * count so every `ContactFlowTable` row stays one line/one height. `pill`
 * order is already priority-ranked server-side (`sortPillsByPriority`,
 * src/services/flow/contact-state.ts) before it crosses the wire, so "show
 * the first N" is already "show the most important N" — no ranking logic
 * needed here. Spencer's literal QA example ("queued + 2 more") pins this
 * at 1; it's a module constant precisely so bumping it later is a one-line
 * change, not a rewrite.
 */
const MAX_VISIBLE_PILLS = 1;

/**
 * Style for the "+N more" overflow chip — reuses the neutral
 * `recent_request` bg/tx pair (Clay semantic tokens only, never a hex) so
 * this doesn't need a brand-new token pair for one chip.
 */
const OVERFLOW_CHIP_STYLE = DIMENSION_STYLES.recent_request;

/**
 * Pill strip for one contact row (the "Terra Actions" table column).
 * `pills` is undefined while the batch fetch is loading, or when the
 * contact's id wasn't in the response (absent = no signal, per the
 * resolver contract) — renders nothing either way: no per-cell spinners
 * (2026-07-23 decision).
 *
 * Deliberately has NO onClick / stopPropagation of its own: a click
 * anywhere in this cell must bubble to the row's onClick (expand into
 * ContactDetail) — pills never navigate directly (2026-07-23 decision, A4).
 * The D15 overflow chip below inherits this contract: it is a plain `<span>`
 * (never a `<button>`) and never calls `stopPropagation` — hiding pills is
 * safe precisely because the row's expand handler shows the full fact list
 * (ContactDetailPanels' Terra Actions strip), so a bubbled click still gets
 * the rep to every hidden signal.
 */
export function StatePills({ pills }: { pills: ContactStatePill[] | undefined }) {
  if (!pills || pills.length === 0) return null;
  // D15: `flex-nowrap min-w-0` (replacing `flex-wrap`) means the strip can
  // never grow the row's height by wrapping onto a second line — the row
  // height guarantee comes from capping pill count, not from CSS alone.
  const visible = pills.slice(0, MAX_VISIBLE_PILLS);
  const hidden = pills.slice(MAX_VISIBLE_PILLS);
  return (
    <div className="flex items-center gap-1 flex-nowrap min-w-0">
      {/* Fix A: an adapter can now emit MORE THAN ONE pill for the same
       * dimension (e.g. 'invited' → "Recent Campaign" + "Suggested Invite") —
       * `dimension` alone is no longer a unique React key. `label` is
       * unique within a dimension by construction (aggregatePills groups
       * facts by label), so the pair is. */}
      {visible.map(pill => <StatePill key={`${pill.dimension}:${pill.label}`} pill={pill} />)}
      {hidden.length > 0 && (
        <span
          data-testid="state-pill-overflow"
          title={hidden.map(pillDisplayLabel).join(' · ')}
          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold whitespace-nowrap shrink-0 ${OVERFLOW_CHIP_STYLE.bg} ${OVERFLOW_CHIP_STYLE.tx}`}
        >
          +{hidden.length} more
        </span>
      )}
    </div>
  );
}

const ACTION_BUTTON_CLASS =
  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-semibold shrink-0 border transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * The "Add to queue" action for a `flow:enroll-suggest:<campaignId>` fact
 * (2026-07-23 decision #3 — the suggested-tier invite is NOT a link, it's a
 * one-click enqueue-with-play). `campaignId` maps to a live PlayOption via
 * `play_instance_ref` (the queue's own play catalog, exposed by
 * EnrollQueueContext); with no match, adds unassigned instead of erroring —
 * the rep assigns the play manually.
 *
 * Already-queued idiom matches `WorkflowsDropdown`'s "✓ Queued" state
 * (`ContactActionMenus.tsx`'s `handleEnroll`/`queued` branch — the same
 * derivation the retired `EnrollButton` used to carry): NOT inert-disabled —
 * the button stays clickable, reads
 * "Queued", and a click just opens the sidebar instead of re-adding
 * (`addQueueItem` is idempotent per contact server-side too, but there's no
 * reason to re-POST when the click's real intent is "show me the queue").
 *
 * Cache invalidation (U-C): a successful add invalidates BOTH contact-state
 * query key families so the pill/strip refetch and reflect the new queued
 * state. This is now CENTRALIZED in `EnrollQueueContext`'s
 * `invalidateContactState` helper (wired into `addContact`'s own mutation —
 * see EnrollQueueContext.tsx) — this component used to run its own ad-hoc
 * `invalidateQueries` calls here directly (pre-U-C), which is exactly the
 * kind of per-surface duplication U-C exists to remove. Do not re-add a
 * local invalidation call here; if a future add path needs different
 * invalidation behavior, change the one helper, not this button.
 *
 * U-B (play-catalog race guard): while the play catalog fetch hasn't
 * SETTLED yet (`!playsResolved`, EnrollQueueContext) and this contact isn't
 * already queued, the button disables with a spinner rather than risking a
 * click that fires before `plays` has loaded — a click at that moment would
 * silently fall back to the unassigned-add path even when a matching live
 * play exists, because `matchedPlay` can only find a match once `plays` is
 * populated. `playsResolved` unlocks on EITHER success or failure of that
 * fetch (never on the queued branch, which needs no catalog at all to know
 * "open the sidebar").
 */
function AddToQueueButton({ contactId, campaignId }: { contactId: string; campaignId: string }) {
  const { queued, pending, catalogLoading, title, onClick } = useAddToQueueFromSuggestion(contactId, campaignId);

  return (
    <button
      onClick={e => { e.stopPropagation(); void onClick(); }}
      disabled={pending || catalogLoading}
      title={title}
      className={`${ACTION_BUTTON_CLASS} ${
        queued
          ? 'border-content-success text-content-success bg-bg-green-light dark:text-emerald-400 dark:border-emerald-700'
          : 'border-secondary dark:border-nightshade-700 text-content-secondary dark:text-nightshade-400 bg-white dark:bg-nightshade-800'
      }`}
    >
      {(pending || catalogLoading) && <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />}
      {queued ? 'Queued' : 'Add to queue'}
    </button>
  );
}

/**
 * The "Add to queue" action for a `flow:warm-intro:<warmIntroId>` fact
 * (warm-intro plan U6, R8 — mirrors `AddToQueueButton` above almost
 * exactly): a click adds the contact with this connector's
 * `salesforce_warm_intro.id` preselected (`addContact`'s `warmIntroId` opt,
 * written into `QueueAddSchema.warm_intro_id` in the same request) plus the
 * `warm_intro` play type itself, resolved from the loaded catalog by
 * `play_type_slug` (not `play_instance_ref` — unlike the suggested-invite
 * card, a warm intro isn't tied to one campaign instance).
 *
 * The `warm_intro` play type row doesn't exist until U7 — until then
 * `matchedPlay` is always null and this degrades to the SAME
 * add-without-play fallback `AddToQueueButton` uses when its campaign has no
 * live match: the contact is added (with the connector still preselected),
 * no play is assigned, and the rep assigns one manually. This is expected
 * within this PR (U6's approach note) — U7 makes the match live.
 *
 * Idempotent-idiom, cache-invalidation, and play-catalog-race-guard
 * (`playsResolved`) all match `AddToQueueButton` verbatim — see its doc
 * comment for the reasoning; not re-explained here to avoid drift between
 * two copies of the same rationale.
 */
function WarmIntroAddButton({ contactId, warmIntroId }: { contactId: string; warmIntroId: string }) {
  const { queued, pending, catalogLoading, title, onClick } = useWarmIntroAdd(contactId, warmIntroId);

  return (
    <button
      onClick={e => { e.stopPropagation(); void onClick(); }}
      disabled={pending || catalogLoading}
      title={title}
      className={`${ACTION_BUTTON_CLASS} ${
        queued
          ? 'border-content-success text-content-success bg-bg-green-light dark:text-emerald-400 dark:border-emerald-700'
          : 'border-secondary dark:border-nightshade-700 text-content-secondary dark:text-nightshade-400 bg-white dark:bg-nightshade-800'
      }`}
    >
      {(pending || catalogLoading) && <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />}
      {queued ? 'Queued' : 'Add to queue'}
    </button>
  );
}

/**
 * The "View request" action for a `flow:request:<executionId>` fact
 * (flow-workflow-requests U8, Key Technical Decision #5 — the Recent Request
 * pill deep-links into the sidebar's Requests tab, not out to another app).
 * `openRequestDetail` (U6's `EnrollQueueContext`) does the whole job in one
 * call: opens the docked sidebar, switches to the Requests tab, and drills
 * straight into that execution's detail view — the same single entry point
 * U7's post-fire flow uses.
 */
function RequestsOpenButton({ requestId }: { requestId: string }) {
  const { openRequestDetail } = useEnrollQueue();
  return (
    <button
      onClick={e => { e.stopPropagation(); openRequestDetail(requestId); }}
      title="Open in Actions"
      className={`${ACTION_BUTTON_CLASS} border-secondary dark:border-nightshade-700 text-content-secondary dark:text-nightshade-400 bg-white dark:bg-nightshade-800`}
    >
      View request →
    </button>
  );
}

/**
 * The "View" action for a `launcher:campaign:<id>` fact (U11, plan
 * 2026-09-24-005 Item G — supersedes the old Playhouse deep-link this scheme
 * used to map to; see state-links.ts's `launcher:campaign` registry entry
 * comment). Opens `CampaignMemberSidebar` IN-PAGE via the module-scope
 * `useCampaignMemberSidebarStore` (U11a) instead of navigating away — the
 * host that actually renders the sidebar (`CampaignMemberSidebarHost`, U11b)
 * is mounted once at the app shell, mirroring `EnrollQueueDock`'s
 * precedent (AppRouter.tsx). `PILL_CLICKED` still fires here, with
 * `link_kind: 'intent'` (this is an in-app action, not an outbound link, but
 * it's the same rep-facing "pill click" signal the retired anchor's onClick
 * used to emit for this same scheme).
 */
function CampaignMemberOpenButton({
  campaignId,
  contactId,
  observedAt,
  dimension,
  evidenceRef,
  surface,
}: {
  campaignId: string;
  contactId: string;
  observedAt: string;
  dimension: StateDimension;
  evidenceRef: string;
  surface: Surface;
}) {
  const { onClick } = useCampaignMemberOpen({ campaignId, contactId, observedAt, dimension, evidenceRef, surface });
  return (
    <button
      type="button"
      onClick={e => { e.stopPropagation(); onClick(); }}
      className="text-content-action dark:text-blueberry-400 hover:underline shrink-0"
    >
      View
    </button>
  );
}

/**
 * Section-level "View all →" control for the Recent Actions section
 * (ContactDetailPanels.tsx's RecentActionsSection — flow-workflow-requests
 * "timestamps + View all"). Rendered there (not inside a StateFactCard —
 * this is a sibling of the ≤2 rendered pills, not itself a fact) whenever
 * the contact has MORE recent_request facts than the render limit shows;
 * opens the docked sidebar on the Requests tab scoped to this contact so the
 * rep sees every run, not just the newest few.
 *
 * `contactId` is what the feed is actually filtered by — an EXACT match
 * (item #7 review finding). `contactName` is display-only (shown in the
 * search box for readability); it is deliberately NOT what the feed queries
 * by anymore — a name-based substring search collided between similarly-
 * named contacts ("Jon" matched "Jonathan") and matched EVERYTHING for a
 * nameless contact (an empty seed string).
 */
export function ViewAllRequestsButton({ contactId, contactName }: { contactId: string; contactName: string }) {
  const { openRequestsForContact } = useEnrollQueue();
  return (
    <button
      type="button"
      onClick={e => { e.stopPropagation(); openRequestsForContact(contactId, contactName); }}
      className="text-[10.5px] font-semibold text-content-action dark:text-blueberry-400 hover:underline shrink-0"
    >
      View all →
    </button>
  );
}

/**
 * One compact fact card for the ContactDetail "Terra Actions" strip
 * (2026-07-23 revision 3 — full-width strip above the detail grid, not a
 * grid panel). Deliberately terse: pill-style header (fixed dimension
 * label — StateFact carries no `label` field, so the header text/icon/tone
 * come from `factCardStyle` (Fix A: "Suggested Invite" for a suggested-tier
 * `invited` fact, "Upcoming Campaign"/"Recent Campaign" for a membership fact
 * by its start_date bucket, "Recent Task" for a closed task, else the fixed
 * per-dimension default)), a body line,
 * and either the deep-link, an in-app action button (resolveEvidenceLink's
 * `intent` kind — decision #3), or the raw evidence_ref as plain text when
 * the scheme is unrecognized (never a guessed URL). No freshness/
 * observed_at/detail-payload here — that level of provenance detail doesn't
 * fit an inline strip card; the fields are still on the StateFact if a
 * future surface needs them.
 *
 * The `queued` dimension is a SPECIAL CASE (U-GF-FE, Spencer 2026-07-23
 * decision — supersedes the earlier owner-only "Open queue" button plan):
 * its body line is the static text "In queue" plus, when known, a muted
 * "by <ownerName>" — never `fact.status` (which carries the assigned play
 * name or the unassigned fallback; that's now considered too much detail for
 * this card) — and it renders NO action button at all, regardless of what
 * `resolveEvidenceLink` says about its `flow:queue` evidence_ref. The
 * `flow:queue` → `queue-open` registry entry itself stays (harmless,
 * future-proof for a surface that DOES want the open-sidebar action) — only
 * this card's rendering ignores it now.
 */
export function StateFactCard({ fact, surface = 'command_center' }: { fact: StateFact; surface?: Surface }) {
  const track = useSegmentTrack();
  const style = factCardStyle(fact);
  const isQueued = fact.dimension === 'queued';
  const link = resolveEvidenceLink(fact.evidence_ref, fact.detail);
  const ownerName = isQueued ? queuedCardOwnerName(fact.detail) : null;
  // Ranking refinement (2026-07-27), updated for the 2026-07-30 display-
  // unification: the "· Gong flow" annotation is scoped to ONLY the closed
  // "Recent Task" variant. An OPEN gong-linked task now renders the same
  // uniform "Open Task" header as a direct task (factCardStyle above no
  // longer branches on linkage for open tasks) — no annotation needed there
  // either way, since nothing in the open-task display distinguishes gong
  // linkage anymore. A CLOSED gong-linked task keeps the "Recent Task"
  // header (closed wins over linkage), which does NOT say "gong", so the
  // annotation still adds real provenance there.
  const isClosedGongLinkedTask =
    fact.dimension === 'open_task' &&
    pillLabelForFact(fact) === RECENT_TASK_LABEL &&
    isGongLinkedOpenTaskFact(fact);

  return (
    <div
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-secondary dark:border-nightshade-700 bg-white dark:bg-nightshade-900 text-[11px]"
      data-testid="state-fact-card"
    >
      <span
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10.5px] font-semibold whitespace-nowrap shrink-0 ${style.bg} ${style.tx}`}
      >
        <span aria-hidden="true">{style.icon}</span>
        {style.label}
      </span>
      {isQueued ? (
        <span className="text-content-primary dark:text-nightshade-100 truncate max-w-[220px] inline-flex items-center gap-1">
          {/* "In queue" and the owner clause are each their own element —
           * not just sibling text nodes under one <span> — so each is
           * independently queryable by its exact text (screen.getByText). */}
          <span>In queue</span>
          {ownerName && (
            <span className="text-content-faint dark:text-nightshade-600">by {ownerName}</span>
          )}
        </span>
      ) : fact.dimension === 'recent_request' ? (
        // flow-workflow-requests ("timestamps + View all"): the run's
        // relative time, appended after the status text — same formatter
        // the Requests feed rows use (request-format.tsx), not a
        // reimplementation. Own <span> (not a sibling text node) so it's
        // independently queryable, mirroring the `isQueued` owner clause
        // above.
        <span className="text-content-primary dark:text-nightshade-100 truncate max-w-[220px] inline-flex items-center gap-1">
          <span className="truncate">{fact.status}</span>
          <span className="text-content-faint dark:text-nightshade-600 shrink-0 whitespace-nowrap">
            · {formatRelativeTime(fact.observed_at)}
          </span>
        </span>
      ) : (
        <span className="text-content-primary dark:text-nightshade-100 truncate max-w-[220px]">{fact.status}</span>
      )}
      {isClosedGongLinkedTask && (
        <span className="text-content-faint dark:text-nightshade-600 shrink-0 whitespace-nowrap">
          · Gong flow
        </span>
      )}
      {isQueued ? null : link.kind === 'url' ? (
        <a
          href={link.url}
          target="_blank"
          rel="noreferrer"
          onClick={e => {
            e.stopPropagation();
            track(ANALYTICS_EVENTS.PILL_CLICKED, {
              dimension: fact.dimension,
              link_kind: 'url',
              evidence_ref: fact.evidence_ref,
              contact_id: fact.contact_key,
              account_id: null,
              surface,
            });
          }}
          className="text-content-action dark:text-blueberry-400 hover:underline shrink-0"
        >
          View ↗
        </a>
      ) : link.kind === 'intent' && link.intent === 'enroll-suggest' ? (
        <AddToQueueButton contactId={fact.contact_key} campaignId={link.campaignId} />
      ) : link.kind === 'intent' && link.intent === 'requests-open' ? (
        <RequestsOpenButton requestId={link.requestId} />
      ) : link.kind === 'intent' && link.intent === 'warm-intro' ? (
        <WarmIntroAddButton contactId={fact.contact_key} warmIntroId={link.warmIntroId} />
      ) : link.kind === 'intent' && link.intent === 'campaign-member-open' ? (
        <CampaignMemberOpenButton
          campaignId={link.campaignId}
          contactId={fact.contact_key}
          observedAt={fact.observed_at}
          dimension={fact.dimension}
          evidenceRef={fact.evidence_ref}
          surface={surface}
        />
      ) : (
        <span className="text-content-faint dark:text-nightshade-600 shrink-0 truncate max-w-[100px]" title={fact.evidence_ref}>
          {fact.evidence_ref}
        </span>
      )}
    </div>
  );
}
